import React, { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import './styles/globals.css'
import { Toaster } from "./components/ui/toaster"
import { useAuth } from './contexts/AuthContext'

const SurveyDesigner = lazy(() => import('./components/SurveyDesigner'))
const PulseDesigner = lazy(() => import('./components/pulse/shell/PulseDesigner'))
const SurveyResponse = lazy(() => import('./components/survey-response'))
const PulseResponse = lazy(() => import('./components/pulse/response/PulseResponse'))
const SharedReportPage = lazy(() => import('./components/pulse/report/SharedReportPage'))
const DemoFlow = lazy(() => import('./components/pulse-demo-flow'))
const ContactUsPage = lazy(() => import('./components/contact-us'))
const SurveySetup = lazy(() => import('./components/SurveySetup'))
const Dashboard = lazy(() => import('./components/Dashboard'))
const LoginPage = lazy(() => import('./components/LoginPage'))
const NetworkReportPreview =
  process.env.NODE_ENV === 'development'
    ? lazy(() => import('./components/pulse/report/NetworkReportPreview'))
    : null
const IndividualReportPreview =
  process.env.NODE_ENV === 'development'
    ? lazy(() => import('./components/pulse/report/IndividualReportPreview'))
    : null
const OrganisationReportPreview =
  process.env.NODE_ENV === 'development'
    ? lazy(() => import('./components/pulse/report/OrganisationReportPreview'))
    : null
const Layout = lazy(() =>
  import('./components/Layout').then((m) => ({ default: m.Layout }))
)

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"
        role="status"
        aria-label="Loading"
      />
    </div>
  )
}

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth()
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  
  return <>{children}</>
}

function DemoResumeRoute() {
  const { token } = useParams<{ token: string }>()
  return <DemoFlow resumeToken={token} />
}

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/demo" element={<DemoFlow />} />
        <Route path="/demo/resume/:token" element={<DemoResumeRoute />} />
        {NetworkReportPreview ? (
          <Route
            path="/dev/network-report"
            element={<NetworkReportPreview />}
          />
        ) : null}
        {IndividualReportPreview ? (
          <Route
            path="/dev/individual-report"
            element={<IndividualReportPreview />}
          />
        ) : null}
        {OrganisationReportPreview ? (
          <Route
            path="/dev/org-report"
            element={<OrganisationReportPreview />}
          />
        ) : null}
        <Route path="/contact-us" element={<ContactUsPage />} />
        <Route 
          path="/respond/:surveyId/:subjectId/:respondentId" 
          element={
            <div className="min-h-screen bg-gray-50">
              <SurveyResponse />
            </div>
          } 
        />
        <Route 
          path="/survey/setup/:setupToken" 
          element={
            <div className="min-h-screen bg-gray-50">
              <SurveySetup />
            </div>
          } 
        />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route path="/pulse/report/:token" element={<SharedReportPage />} />
        <Route
          path="/pulse/respond/:token"
          element={
            <div className="min-h-screen bg-gray-50">
              <PulseResponse />
            </div>
          }
        />
        <Route
          path="/pulse/:pulseId"
          element={
            <PrivateRoute>
              <Layout headerless>
                <PulseDesigner />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/survey/:surveyId"
          element={
            <PrivateRoute>
              <Layout headerless>
                <SurveyDesigner />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/survey/new"
          element={
            <PrivateRoute>
              <Layout headerless>
                <SurveyDesigner />
              </Layout>
            </PrivateRoute>
          }
        />
      </Routes>
      </Suspense>
      <Toaster />
    </AuthProvider>
  )
}

export default App 